import { useEffect, useState } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { loadSession } from '../firebase/session';
import { startSync, stopSync, pushCanvasState } from '../firebase/sync';
import { BASE_VB, canvasNodeToProcessNode } from '../types';

interface UseSessionOptions {
    sessionId: string;
    uid: string;
    cursorId: string;
    /** Si es true, cualquier cambio local se escribe en Firestore */
    syncEnabled?: boolean;
}

export interface UseSessionResult {
    loading: boolean;
    error: string | null;
    ready: boolean;
}

export function useSession({ sessionId, uid, cursorId, syncEnabled = true }: UseSessionOptions): UseSessionResult {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [ready, setReady] = useState(false);

    // ── Carga inicial + suscripción ───────────────────────────────────────────

    useEffect(() => {
        let cancelled = false;

        async function init() {
            setLoading(true);
            setError(null);

            try {
                const data = await loadSession(sessionId);
                if (cancelled) return;

                if (!data) {
                    setError('Sesión no encontrada');
                    setLoading(false);
                    return;
                }

                if (data.status === 'closed') {
                    setError('Esta sesión ya ha finalizado');
                    setLoading(false);
                    return;
                }

                // Carga el estado inicial en el store
                useSessionStore.getState().loadState({
                    version: 1,
                    nodes: Object.values(data.nodes ?? {}).map(canvasNodeToProcessNode),
                    layout: {
                        nodes: Object.values(data.nodes ?? {}),
                        // El viewBox no se comparte entre participantes.
                        // Cada cliente arranca con su propia cámara local.
                        viewBox: { ...BASE_VB },
                    },
                    processes: data.processes ?? [],
                    edges: data.edges ?? [],
                    people: data.people ?? [],
                });

                if (syncEnabled) startSync(sessionId, uid, cursorId);
                setReady(true);
            } catch (e: any) {
                if (!cancelled) setError(e.message ?? 'Error de conexión');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        init();
        return () => {
            cancelled = true;
            stopSync();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, uid, cursorId]);

    // ── Publicar cambios locales → Firestore ──────────────────────────────────
    // Nos suscribimos al store y empujamos cada cambio

    useEffect(() => {
        if (!ready || !syncEnabled) return;

        const unsub = useSessionStore.subscribe((state, prevState) => {
            const canvasChanged =
                state.nodes !== prevState.nodes ||
                state.processes !== prevState.processes ||
                state.edges !== prevState.edges ||
                state.people !== prevState.people;

            if (canvasChanged) pushCanvasState();
        });

        return unsub;
    }, [ready, syncEnabled]);

    return { loading, error, ready };
}
